import { useEffect } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router';

import { PageLoader } from '@/components/common';
import { initAnalytics, trackEntranceAnalytics } from '@/utils/analytics';
import { logger } from '@/utils/logger';

const LOG_PREFIX = '[LoginPage]';

/**
 * Extract invite-related params from a URL string.
 * Returns { subscriber, invitationKey, access } — whichever are present.
 */
const extractInviteParams = (
  url: string
): { subscriber?: string; invitationKey?: string; access?: string } | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    const subscriber = parsed.searchParams.get('subscriber');
    const invitationKey = parsed.searchParams.get('invitationKey');
    const access = parsed.searchParams.get('access');
    if (subscriber || invitationKey || access) {
      return {
        ...(subscriber ? { subscriber } : {}),
        ...(invitationKey ? { invitationKey } : {}),
        ...(access ? { access } : {}),
      };
    }
  } catch {
    // not a valid URL — ignore
  }
  return null;
};

export const LoginPage = () => {
  const { loginWithRedirect, isLoading } = useAuth0();
  const [searchParams] = useSearchParams();

  logger.info(LOG_PREFIX, 'render', { isLoading, url: window.location.href });

  useEffect(() => {
    if (!isLoading) {
      const returnTo = searchParams.get('returnTo');

      if (returnTo) {
        sessionStorage.setItem('auth_return_to', returnTo);
      }

      // Try to extract invite params from either the encoded returnTo or the current URL
      const decodedReturnTo = returnTo
        ? (() => {
            try {
              return atob(returnTo);
            } catch {
              return '';
            }
          })()
        : '';
      const currentUrl = window.location.pathname + window.location.search;

      const invite = extractInviteParams(decodedReturnTo) ?? extractInviteParams(currentUrl);

      const hasSubscriber = !!invite?.subscriber;
      const hasInvitationKey = !!invite?.invitationKey;
      const hasAccess = !!invite?.access;
      const isInvite = hasSubscriber || hasInvitationKey || hasAccess;

      logger.info(LOG_PREFIX, 'Login triggered', {
        returnTo: decodedReturnTo || '(none)',
        currentUrl,
        subscriber: invite?.subscriber || '(none)',
        invitationKey: invite?.invitationKey || '(none)',
        access: invite?.access || '(none)',
        isInvite,
      });

      // Track entrance analytics — mirrors legacy dataProtect trackEntranceAnalytics()
      if (isInvite) {
        initAnalytics();
        trackEntranceAnalytics({
          subscriber: invite?.subscriber,
          invitationKey: invite?.invitationKey,
          access: invite?.access,
        });
      }

      // Persist subscriber key for post-callback API call (addnewsubscriberexistingusertotenant)
      if (hasSubscriber) {
        sessionStorage.setItem('invite_subscriber_key', invite!.subscriber!);
        logger.info(LOG_PREFIX, 'Stored subscriber key in sessionStorage:', invite!.subscriber);
      }
      if (hasInvitationKey) {
        sessionStorage.setItem('invite_invitation_key', invite!.invitationKey!);
        logger.info(LOG_PREFIX, 'Stored invitationKey in sessionStorage:', invite!.invitationKey);
      }

      // Build custom Auth0 authorization params — mirrors legacy dataProtect extractCustomParams()
      // subscriber / invitationkey / access are passed to Auth0 Rules/Actions for server-side processing
      const authorizationParams: Record<string, string> = {};
      if (isInvite) {
        authorizationParams.screen_hint = 'signup';
        authorizationParams.signup = 'true';
      }
      if (hasSubscriber) {
        authorizationParams.subscriber = invite!.subscriber!;
      }
      if (hasInvitationKey) {
        authorizationParams.invitationkey = invite!.invitationKey!;
      }
      if (hasAccess) {
        authorizationParams.access = invite!.access!;
      }

      logger.info(
        LOG_PREFIX,
        'Calling loginWithRedirect with authorizationParams:',
        authorizationParams
      );

      loginWithRedirect({
        authorizationParams:
          Object.keys(authorizationParams).length > 0 ? authorizationParams : undefined,
      });
    }
  }, [isLoading, loginWithRedirect, searchParams]);

  return <PageLoader variant="viewport" message="Redirecting to sign in..." />;
};
